import {
  ApolloQueryResult,
  OperationVariables,
  useLazyQuery,
  useMutation
} from '@apollo/client'
import {
  Button,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import AttachMoneyIcon from '@material-ui/icons/AttachMoney'
import MinimizeRoundedIcon from '@material-ui/icons/MinimizeRounded'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import { addJob } from '../../cache/jobs'
import SectionTitle from '../../components/SectionTitle'
import {
  CANCEL_PROFORMA_MUTATION,
  CONFIRM_PROFORMA_MUTATION,
  GET_PREVIEW_OF_REFRESHED_PROFORMA,
  OFFLINE_PAYMENT_OF_PROFORMA_MUTATION,
  ONLINE_PAYMENT_OF_PROFORMA_MUTATION,
  RETRY_PAYMENT_OF_PROFORMA_MUTATION
} from '../../graphql/walletPaymemt'
import { getTodayDate } from '../../helpers/date'
import { getTranslate } from '../../localization'
import { JobsType } from '../../mockData/jobs'
import { LocalizedLink } from '../../routes/localizedRoutes'
import { Wallet } from '../../types/wallet'
import {
  GetPreviewOfRefreshedProformaResponse,
  Proforma,
  RetryPaymentOfProformaResponse
} from '../../types/walletProforma'
import useStyles from './styles/SummeryOfPayment.styles'

interface SummeryOfPaymentProps {
  proforma: Proforma
  wallet: Wallet
  creditorName?: string
  isOnlinePayment?: boolean
  refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<
    ApolloQueryResult<{
      proforma: Proforma
    }>
  >
  attachments: string[]
}

const SummeryOfPayment: React.FC<SummeryOfPaymentProps> = ({
  proforma,
  wallet,
  creditorName,
  isOnlinePayment,
  refetch,
  attachments
}) => {
  const classes = useStyles()
  const history = useHistory()
  const [confirmProforma] = useMutation(CONFIRM_PROFORMA_MUTATION, {
    onCompleted: (resp) => {
      toast.success(getTranslate(`${resp.confirmProforma.message}`))
      refetch()
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })
  const [onlinePayment] = useMutation(ONLINE_PAYMENT_OF_PROFORMA_MUTATION, {
    onCompleted: (resp) => {
      const newJob: JobsType = {
        trackID: resp.onlinePaymentOfProforma.trackingID,
        creationDate: getTodayDate(),
        status: 'InProgress'
      }
      addJob(newJob)
      toast.info(
        getTranslate(
          `payment job with tracking id ${resp.onlinePaymentOfProforma.trackingID} started`
        )
      )
      refetch()
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const [offlinePayment] = useMutation(OFFLINE_PAYMENT_OF_PROFORMA_MUTATION, {
    onCompleted: () => {
      toast.success(
        getTranslate(`offline payment sucessfull wait until it is confirmed`)
      )
      refetch()
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })
  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false)
  const [previewData, setPreviewData] =
    useState<GetPreviewOfRefreshedProformaResponse | null>(null)

  const [getPreviewOfRefreshedProforma] = useLazyQuery<{
    getPreviewOfRefreshedProforma: GetPreviewOfRefreshedProformaResponse
  }>(GET_PREVIEW_OF_REFRESHED_PROFORMA, {
    onCompleted: (data) => {
      setPreviewData(data.getPreviewOfRefreshedProforma)
      setIsRenewModalOpen(true)
    },
    onError: (error) => {
      toast.error(error.message)
    }
  })

  const [cancelProforma] = useMutation(CANCEL_PROFORMA_MUTATION, {
    onCompleted: (resp) => {
      toast.success(getTranslate(`${resp.cancelProforma.message}`))
      refetch()
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const [retryPaymentOfProforma] = useMutation<RetryPaymentOfProformaResponse>(
    RETRY_PAYMENT_OF_PROFORMA_MUTATION,
    {
      onCompleted: (data) => {
        toast.success(getTranslate('Proforma renewed successfully'))
        history.push(
          `/adopay/wallet/${wallet.walletID}/invoice-list/${data.newproformaID}/details`
        )
      },
      onError: (error) => {
        toast.error(error.message)
      }
    }
  )
  const totalAmount = proforma?.transactions.reduce(
    (sum, transaction) => sum + transaction.amount,
    0
  )

  const [expiresIn, setExpiresIn] = useState<number>(
    Math.max(
      0,
      Math.floor(
        (new Date(
          proforma.status === 'pendingConfirmation'
            ? proforma.expiresAt
            : proforma.paymentDeadline
        ).getTime() -
          Date.now()) /
          1000
      )
    )
  )

  useEffect(() => {
    const timer = setInterval(() => {
      setExpiresIn((prev) => Math.max(0, prev - 1))
    }, 1000)

    return () => clearInterval(timer)
  }, [proforma.expiresAt])
  const onConfirm = () => {
    confirmProforma({
      variables: { proformaID: proforma.proformaID }
    })
  }
  const hasTransactionsInOtherProformas = proforma.transactions.some(
    (tx) => tx.proformaID && tx.proformaID !== proforma.proformaID
  )
  const onRenew = () => {
    getPreviewOfRefreshedProforma({
      variables: { proformaID: proforma.proformaID }
    })
  }
  const onPay = () => {
    if (isOnlinePayment) {
      onlinePayment({
        variables: { proformaID: proforma.proformaID }
      })
    } else if (attachments.length > 0) {
      offlinePayment({
        variables: {
          input: { proformaID: proforma.proformaID, attachments: attachments }
        }
      })
    }
  }

  const onCancel = () => {
    cancelProforma({
      variables: { proformaID: proforma.proformaID }
    })
  }
  const getMainActionButton = () => {
    if (['expired', 'rejected', 'cancelled'].includes(proforma.status)) {
      return {
        label: getTranslate('Renew Proforma'),
        onClick: onRenew,
        disabled: hasTransactionsInOtherProformas,
        tooltip: hasTransactionsInOtherProformas
          ? getTranslate('Some of the transactions are in other proformas')
          : undefined
      }
    }

    if (proforma.status === 'pendingConfirmation') {
      return {
        label: getTranslate('Confirm'),
        onClick: onConfirm
      }
    }

    if (proforma.status === 'pendingPayment') {
      const payLabel = isOnlinePayment
        ? getTranslate('Pay Online')
        : getTranslate('Pay Offline')
      return {
        label: payLabel,
        onClick: onPay
      }
    }

    // Default: hide button if no valid status
    return null
  }

  const mainActionButton = getMainActionButton()
  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    const pad = (n: number) => n.toString().padStart(2, '0')

    return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`
  }
  return (
    <div>
      {' '}
      <SectionTitle title="Summary of Payment" marginBottom />
      <div className={classes.root}>
        <div className={classes.sectionRow}>
          <Typography variant="body2">
            <MinimizeRoundedIcon />

            {getTranslate('Wallet Entity')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {wallet.entity.entityType + ' - ' + wallet.entity.entityName}
          </Typography>
        </div>
        <div className={classes.sectionRow}>
          <Typography variant="body2">
            {' '}
            <MinimizeRoundedIcon />
            {getTranslate('Wallet Type')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {wallet.walletType}
          </Typography>
        </div>

        {wallet.isBilateral && creditorName && (
          <div className={classes.sectionRow}>
            <Typography variant="body2">
              {' '}
              <MinimizeRoundedIcon />
              {getTranslate('Creditor')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {creditorName}
            </Typography>
          </div>
        )}

        <Box className={classes.paymentBox}>
          <div className={classes.paymentRow}>
            <Box display="flex" alignItems="center">
              <AttachMoneyIcon fontSize="small" className={classes.icon} />
              <Typography variant="body2">
                {getTranslate('Total Payment')}
              </Typography>
            </Box>
            <Typography variant="body2">
              {totalAmount + ' ' + wallet.currency}
            </Typography>
          </div>
          {(proforma.status == 'pendingConfirmation' ||
            proforma.status == 'pendingPayment') && (
            <div className={classes.paymentRow}>
              <Box display="flex" alignItems="center">
                <AccessTimeIcon fontSize="small" className={classes.icon} />
                <Typography variant="body2">
                  {getTranslate(
                    proforma.status == 'pendingConfirmation'
                      ? 'Expires in'
                      : 'payment deadline'
                  )}
                </Typography>
              </Box>
              <Typography variant="body2" className={classes.expiresTime}>
                {formatTime(expiresIn)}
                ’’
              </Typography>
            </div>
          )}
        </Box>

        {mainActionButton && (
          <Button
            disabled={
              mainActionButton.disabled ??
              (!isOnlinePayment &&
                attachments.length === 0 &&
                proforma.status == 'pendingPayment')
            }
            className={classes.confirmButton}
            variant="contained"
            color="primary"
            fullWidth
            onClick={mainActionButton.onClick}>
            {mainActionButton.label}
          </Button>
        )}
        {proforma.status === 'paid' && (
          <Button
            className={classes.confirmButton}
            variant="contained"
            color="primary"
            fullWidth
            component={LocalizedLink}
            to={`/adopay/wallet/${wallet.walletID}/invoice-list/${proforma.proformaID}/receipt`}>
            {getTranslate('Receipt')}
          </Button>
        )}
        {hasTransactionsInOtherProformas && proforma.status !== 'paid' && (
          <Typography
            color="error"
            variant="body2"
            style={{ marginTop: '0.5rem' }}>
            {getTranslate(
              'Some of the transactions are currently in other proformas'
            )}
          </Typography>
        )}

        {(proforma.status == 'pendingConfirmation' ||
          proforma.status == 'pendingPayment' ||
          proforma.status == 'pendingPaymentConfirmation') && (
          <Button
            className={classes.cancelButton}
            variant="outlined"
            color="default"
            fullWidth
            onClick={onCancel}>
            {getTranslate('Cancel')}
          </Button>
        )}
      </div>
      <Dialog
        open={isRenewModalOpen}
        onClose={() => setIsRenewModalOpen(false)}>
        <DialogTitle>{getTranslate('Renew Proforma')}</DialogTitle>
        <DialogContent>
          <Typography>
            {getTranslate('Previous Amount')}:{' '}
            {previewData?.previousAmountOfPayment} {wallet.currency}
          </Typography>
          <Typography>
            {getTranslate('New Amount')}: {previewData?.totalAmountOfPayment}{' '}
            {wallet.currency}
          </Typography>
          <Typography>
            {getTranslate('Do you want to proceed with the renewal?')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRenewModalOpen(false)} color="secondary">
            {getTranslate('Cancel')}
          </Button>
          <Button
            onClick={() =>
              retryPaymentOfProforma({
                variables: { proformaID: proforma.proformaID }
              })
            }
            color="primary">
            {getTranslate('Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default SummeryOfPayment
